/**
 * useDocumentHead — lightweight per-route <head> manager.
 *
 * No react-helmet dependency (matches this app's dependency-light, custom-router
 * style). Sets document.title and upserts the description, canonical link, and
 * Open Graph / Twitter tags for the current page so each route has unique SEO
 * metadata instead of the single static <title>Persona</title>.
 *
 * Web-only: the Chrome extension renders AIMail directly (extension-entry.jsx)
 * and never mounts App, so this hook never runs in the side panel.
 */
import { useEffect } from 'react';
import { SITE_URL } from '../seo/routes.js';

export { SITE_URL };

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useDocumentHead({ title, description, path, canonical, ogImage, noindex } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    const url = canonical || (path ? SITE_URL + path : undefined);
    const img = ogImage || `${SITE_URL}/persona-homepage-loop-og.jpg`;

    if (title) {
      upsertMeta('property', 'og:title', title);
      upsertMeta('name', 'twitter:title', title);
    }
    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }
    if (url) {
      upsertLink('canonical', url);
      upsertMeta('property', 'og:url', url);
    }
    upsertMeta('property', 'og:image', img);
    upsertMeta('name', 'twitter:image', img);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    // App/admin/invite routes pass noindex; everything public indexes normally.
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
  }, [title, description, path, canonical, ogImage, noindex]);
}
